import { strayLabelsCypherExpr } from './stray'

/**
 * Detail bundle for a single PLC node — works for any of
 * Controller / Program / Routine / AOI / PLCDataType /
 * PLCDataTypeMember / PLCTag. Dispatches on `labels(n)` server-side
 * so the client can issue one round-trip regardless of the selected
 * node's kind, and switches over the discriminated
 * {@link PlantPlcNodeDetail} on the way back to render the right
 * sub-view in {@link PlcDetailPanel}.
 *
 * We can't gate each per-kind CALL with `WITH n WHERE 'Kind' IN
 * labels(n)` because in Cypher that filter wipes the inner row
 * entirely for the four labels the selected node isn't, which makes
 * the subquery return zero rows; multiple CALL blocks then
 * cross-product to zero and the entire detail query yields no result.
 * Instead, every CALL below always emits exactly one row (thanks to
 * `collect` / `count` aggregation) and the label gate is moved into
 * the OPTIONAL MATCH's WHERE clause, so non-applicable matches simply
 * collapse to empty lists / null scalars and the outer RETURN has a
 * single concrete row to project from.
 *
 * Params: `{ nodeId: string }`.
 */
export const PLANT_PLC_NODE_DETAIL_CYPHER = `
            MATCH (n {id: $nodeId})
              WHERE (n:Controller OR n:Program OR n:Routine OR n:AOI
                     OR n:PLCDataType OR n:PLCDataTypeMember OR n:PLCTag)
            WITH n, labels(n) AS labelList
            // Controller -> programs[]
            CALL {
              WITH n
              OPTIONAL MATCH (p:Program)-[:BELONGS_TO]->(n)
                WHERE 'Controller' IN labels(n)
              RETURN collect(distinct p) AS controllerPrograms
            }
            // Controller -> aois[]
            CALL {
              WITH n
              OPTIONAL MATCH (a:AOI)-[:BELONGS_TO]->(n)
                WHERE 'Controller' IN labels(n)
              RETURN collect(distinct a) AS controllerAois
            }
            // Controller -> dataTypes[]
            CALL {
              WITH n
              OPTIONAL MATCH (dt:PLCDataType)-[:BELONGS_TO]->(n)
                WHERE 'Controller' IN labels(n)
              RETURN collect(distinct dt) AS controllerDataTypes
            }
            // Controller -> patterns[]
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:HAS_PATTERN]->(cp:ControlPattern)
                WHERE 'Controller' IN labels(n)
              RETURN collect(distinct cp) AS controllerPatterns
            }
            // Controller -> recursive PLCTag count (Controller itself +
            // every Program/AOI directly under it + every Routine of
            // those Programs). Matches the same shape the tree's
            // per-row tagCount uses so the detail header's "Tags" stat
            // stays consistent with the value the user clicked on in
            // the rail.
            CALL {
              WITH n
              OPTIONAL MATCH (n)<-[:BELONGS_TO*0..2]-(desc)-[:HAS_TAG]->(ct:PLCTag)
                WHERE 'Controller' IN labels(n)
                  AND (desc:Controller OR desc:Program OR desc:Routine OR desc:AOI)
              RETURN count(distinct ct) AS controllerTagCount
            }
            // Controller -> mapped Compute nodes (opc/device) via MAPS_TO_CONTROLLER
            CALL {
              WITH n
              OPTIONAL MATCH (mc:Compute)-[:MAPS_TO_CONTROLLER]->(n)
                WHERE 'Controller' IN labels(n)
              RETURN collect(distinct mc) AS controllerMappedComputes
            }
            // Program -> owning Controller
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:BELONGS_TO]->(progC:Controller)
                WHERE 'Program' IN labels(n)
              RETURN collect(progC)[0] AS programController
            }
            // Program -> routines[]
            CALL {
              WITH n
              OPTIONAL MATCH (r:Routine)-[:BELONGS_TO]->(n)
                WHERE 'Program' IN labels(n)
              RETURN collect(distinct r) AS programRoutines
            }
            // Program -> direct tags[]
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:HAS_TAG]->(pt:PLCTag)
                WHERE 'Program' IN labels(n)
              RETURN collect(distinct pt) AS programTags
            }
            // Program -> recursive PLCTag count (Program + its Routines).
            // Matches the tree row's tagCount so the detail header stays
            // consistent with the value the user clicked on.
            CALL {
              WITH n
              OPTIONAL MATCH (n)<-[:BELONGS_TO*0..1]-(progDesc)-[:HAS_TAG]->(prt:PLCTag)
                WHERE 'Program' IN labels(n)
                  AND (progDesc:Program OR progDesc:Routine)
              RETURN count(distinct prt) AS programRecursiveTagCount
            }
            // Routine -> owning Program + Controller
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:BELONGS_TO]->(rp:Program)
                WHERE 'Routine' IN labels(n)
              OPTIONAL MATCH (rp)-[:BELONGS_TO]->(rc:Controller)
              RETURN collect(rp)[0] AS routineProgram,
                     collect(rc)[0] AS routineController
            }
            // Routine -> direct tags[]
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:HAS_TAG]->(rt:PLCTag)
                WHERE 'Routine' IN labels(n)
              RETURN collect(distinct rt) AS routineTags
            }
            // Routine -> tags this routine reads / writes (def-use).
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:READS_TAG]->(rrt:PLCTag)
                WHERE 'Routine' IN labels(n)
              RETURN collect(distinct rrt) AS routineReadsTags
            }
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:WRITES_TAG]->(rwt:PLCTag)
                WHERE 'Routine' IN labels(n)
              RETURN collect(distinct rwt) AS routineWritesTags
            }
            // AOI -> owning Controller
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:BELONGS_TO]->(ac:Controller)
                WHERE 'AOI' IN labels(n)
              RETURN collect(ac)[0] AS aoiController
            }
            // AOI -> direct tags[]
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:HAS_TAG]->(at:PLCTag)
                WHERE 'AOI' IN labels(n)
              RETURN collect(distinct at) AS aoiTags
            }
            // AOI -> instantiated PLCTag count
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:INSTANTIATES]->(it:PLCTag)
                WHERE 'AOI' IN labels(n)
              RETURN count(distinct it) AS aoiInstantiatedTagCount
            }
            // PLCDataType -> owning Controller
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:BELONGS_TO]->(dtc:Controller)
                WHERE 'PLCDataType' IN labels(n)
              RETURN collect(dtc)[0] AS dataTypeController
            }
            // PLCDataType -> members[]
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:HAS_MEMBER]->(dtm:PLCDataTypeMember)
                WHERE 'PLCDataType' IN labels(n)
              RETURN collect(distinct dtm) AS dataTypeMembers
            }
            // PLCDataType -> mapped Ignition UDT (MAPS_TO_TYPE)
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:MAPS_TO_TYPE]->(udt:UDTDefinition)
                WHERE 'PLCDataType' IN labels(n)
              RETURN collect(udt)[0] AS dataTypeMappedUdt
            }
            // PLCDataType -> tags typed as this structure (OF_TYPE)
            CALL {
              WITH n
              OPTIONAL MATCH (dtt:PLCTag)-[:OF_TYPE]->(n)
                WHERE 'PLCDataType' IN labels(n)
              RETURN collect(distinct dtt) AS dataTypeTags
            }
            // PLCDataTypeMember -> parent PLCDataType + Controller
            CALL {
              WITH n
              OPTIONAL MATCH (pdt:PLCDataType)-[:HAS_MEMBER]->(n)
                WHERE 'PLCDataTypeMember' IN labels(n)
              OPTIONAL MATCH (pdt)-[:BELONGS_TO]->(pdtc:Controller)
              RETURN collect(pdt)[0] AS memberDataType,
                     collect(pdtc)[0] AS memberController
            }
            // PLCTag -> owner (Controller / Program / Routine / AOI)
            CALL {
              WITH n
              OPTIONAL MATCH (owner)-[:HAS_TAG]->(n)
                WHERE 'PLCTag' IN labels(n)
                  AND (owner:Controller OR owner:Program OR owner:Routine OR owner:AOI)
              WITH collect(owner)[0] AS pickedOwner
              RETURN pickedOwner AS tagOwner,
                     CASE WHEN pickedOwner IS NULL
                          THEN NULL
                          ELSE head(labels(pickedOwner))
                     END AS tagOwnerKind
            }
            // PLCTag -> structured controller data type
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:OF_TYPE]->(tdt:PLCDataType)
                WHERE 'PLCTag' IN labels(n)
              RETURN collect(tdt)[0] AS tagDataType
            }
            // PLCTag -> cross-layer SCADA Tag mappings.
            //
            // Per the big comment at the top of this query: every CALL
            // here must always emit exactly one row even when the
            // selected node is not the kind this block is concerned
            // with, otherwise the outer cross-product collapses to
            // zero rows and the whole detail query yields no result.
            // We cannot reuse the earlier blocks' collect(distinct
            // <node>) shortcut here because we want a {tag, edge}
            // shape -- so the null guard is moved INSIDE the collect
            // via a list comprehension on a single aggregated row,
            // instead of an upstream WITH ... WHERE ... IS NOT NULL
            // filter that would wipe the row entirely on non-PLCTag
            // nodes. The earlier upstream-filter shape was a latent
            // bug that only surfaced once we pinned an explicit
            // LIMIT on the outer query (the planner stops papering
            // over "ungrouped aggregation over an empty stream" in
            // CALL subqueries when a strict pipeline is forced).
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[mapEdge:MAPS_TO_TAG]->(st:Tag)
                WHERE 'PLCTag' IN labels(n)
              // Resolve the SCADA tag's hosting compute + provider so
              // the chip can deep-link into
              // /plant/compute/hosts/<host>?provider=<tp>&tag=<name>
              // instead of bouncing through the graph viewer. Both
              // matches are OPTIONAL — legacy tags with no provider or
              // un-attached providers degrade to a graph-viewer link
              // on the client.
              OPTIONAL MATCH (stTp:TagProvider)-[:PROVIDES]->(st)
              OPTIONAL MATCH (stHost:Compute)-[:HAS_TAG_PROVIDER]->(stTp)
              WITH collect({
                tag: st,
                edge: CASE WHEN mapEdge IS NULL THEN NULL ELSE type(mapEdge) END,
                sourceId: n.id,
                props: CASE WHEN mapEdge IS NULL THEN {} ELSE properties(mapEdge) END,
                providerId: CASE WHEN stTp IS NULL THEN NULL ELSE stTp.id END,
                hostId: CASE WHEN stHost IS NULL THEN NULL ELSE stHost.id END,
                hostUrl: CASE WHEN stHost IS NULL THEN NULL ELSE coalesce(stHost.url, stHost.gateway_url) END,
                project: CASE WHEN stHost IS NULL THEN NULL ELSE coalesce(stHost.web_dev_project, stHost.project) END
              }) AS rawRows
              RETURN [r IN rawRows WHERE r.tag IS NOT NULL] AS scadaTagRows
            }
            // PLCTag -> outgoing logic-edge neighbours.  Same
            // null-guard-after-aggregation pattern as scadaTagRows
            // above; see that block for why.
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[outE:TRIGGERS|GATES|INHIBITS|CONTROLS]->(outT:PLCTag)
                WHERE 'PLCTag' IN labels(n)
              WITH collect({ edge: CASE WHEN outE IS NULL THEN NULL ELSE type(outE) END, target: outT, outgoing: true }) AS rawRows
              RETURN [r IN rawRows WHERE r.target IS NOT NULL] AS outRows
            }
            // PLCTag -> incoming logic-edge neighbours.
            CALL {
              WITH n
              OPTIONAL MATCH (inT:PLCTag)-[inE:TRIGGERS|GATES|INHIBITS|CONTROLS]->(n)
                WHERE 'PLCTag' IN labels(n)
              WITH collect({ edge: CASE WHEN inE IS NULL THEN NULL ELSE type(inE) END, target: inT, outgoing: false }) AS rawRows
              RETURN [r IN rawRows WHERE r.target IS NOT NULL] AS inRows
            }
            // PLCTag -> routines that read / write this tag (inverse def-use).
            CALL {
              WITH n
              OPTIONAL MATCH (rbr:Routine)-[:READS_TAG]->(n)
                WHERE 'PLCTag' IN labels(n)
              RETURN collect(distinct rbr) AS tagReadByRoutines
            }
            CALL {
              WITH n
              OPTIONAL MATCH (wbr:Routine)-[:WRITES_TAG]->(n)
                WHERE 'PLCTag' IN labels(n)
              RETURN collect(distinct wbr) AS tagWrittenByRoutines
            }
            // PLCTag -> derivation via CALCULATED_BY (outbound = calculated from).
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[c:CALCULATED_BY]->(src:PLCTag)
                WHERE 'PLCTag' IN labels(n)
              WITH collect({
                tag: src,
                viaRoutineUrn: CASE WHEN c IS NULL THEN NULL ELSE c.via_routine_urn END,
                viaRungNumber: CASE WHEN c IS NULL THEN NULL ELSE c.via_rung_number END
              }) AS rawRows
              RETURN [r IN rawRows WHERE r.tag IS NOT NULL] AS calculatedFromRows
            }
            // PLCTag -> tags that are calculated from this one (inbound).
            CALL {
              WITH n
              OPTIONAL MATCH (dst:PLCTag)-[c:CALCULATED_BY]->(n)
                WHERE 'PLCTag' IN labels(n)
              WITH collect({
                tag: dst,
                viaRoutineUrn: CASE WHEN c IS NULL THEN NULL ELSE c.via_routine_urn END,
                viaRungNumber: CASE WHEN c IS NULL THEN NULL ELSE c.via_rung_number END
              }) AS rawRows
              RETURN [r IN rawRows WHERE r.tag IS NOT NULL] AS feedsRows
            }
            // PLCTag -> robot IO signal mappings.
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[m:MAPS_TO_ROBOT_SIGNAL]->(sig:RobotIOSignal)
                WHERE 'PLCTag' IN labels(n)
              WITH collect({
                signal: sig,
                method: CASE WHEN m IS NULL THEN NULL ELSE m.method END,
                confidence: CASE WHEN m IS NULL THEN NULL ELSE m.confidence END,
                register: CASE WHEN m IS NULL THEN NULL ELSE m.register END,
                direction: CASE WHEN m IS NULL THEN NULL ELSE m.direction END
              }) AS rawRows
              RETURN [r IN rawRows WHERE r.signal IS NOT NULL] AS robotSignalRows
            }
            // SCADA Tag mappings indexed by PLCTag id, for every PLCTag
            // surfaced in the per-kind tables above (so the PlcTagTable
            // can render a chip column next to each row). The per-kind
            // gates inside the CASE keep this empty for PLCTag detail
            // (which already exposes scadaTagRows for its own table)
            // and for any unsupported label.
            //
            // The [NULL] sentinel in the UNWIND guarantees this CALL
            // always emits exactly one outer row even when the union
            // is empty — same single-row guarantee the surrounding
            // CALLs rely on so the outer cross-product never collapses
            // to zero.
            CALL {
              WITH n, labelList, programTags, routineTags, aoiTags
              WITH n,
                   (CASE
                     WHEN 'Program' IN labelList THEN programTags
                     WHEN 'Routine' IN labelList THEN routineTags
                     WHEN 'AOI' IN labelList THEN aoiTags
                     ELSE []
                   END) AS tablePts
              UNWIND (CASE WHEN size(tablePts) = 0 THEN [NULL] ELSE tablePts END) AS pt
              OPTIONAL MATCH (pt)-[mapEdge:MAPS_TO_TAG]->(st:Tag)
                WHERE pt IS NOT NULL
              // Same provider/host resolution as the per-PLCTag
              // scadaTagRows block above — see that block for why
              // both matches are OPTIONAL.
              OPTIONAL MATCH (stTp:TagProvider)-[:PROVIDES]->(st)
              OPTIONAL MATCH (stHost:Compute)-[:HAS_TAG_PROVIDER]->(stTp)
              WITH collect({
                plcTagId: CASE WHEN pt IS NULL THEN NULL ELSE pt.id END,
                tag: st,
                edge: CASE WHEN mapEdge IS NULL THEN NULL ELSE type(mapEdge) END,
                sourceId: CASE WHEN pt IS NULL THEN NULL ELSE pt.id END,
                props: CASE WHEN mapEdge IS NULL THEN {} ELSE properties(mapEdge) END,
                providerId: CASE WHEN stTp IS NULL THEN NULL ELSE stTp.id END,
                hostId: CASE WHEN stHost IS NULL THEN NULL ELSE stHost.id END,
                hostUrl: CASE WHEN stHost IS NULL THEN NULL ELSE coalesce(stHost.url, stHost.gateway_url) END,
                project: CASE WHEN stHost IS NULL THEN NULL ELSE coalesce(stHost.web_dev_project, stHost.project) END
              }) AS rawRows
              RETURN [r IN rawRows WHERE r.tag IS NOT NULL AND r.plcTagId IS NOT NULL]
                AS plcTagScadaRows
            }
            RETURN n, labelList,
                   controllerPrograms, controllerAois, controllerDataTypes,
                   controllerPatterns,
                   controllerTagCount, controllerMappedComputes,
                   programController, programRoutines, programTags,
                   programRecursiveTagCount,
                   routineProgram, routineController, routineTags,
                   routineReadsTags, routineWritesTags,
                   aoiController, aoiTags, aoiInstantiatedTagCount,
                   dataTypeController, dataTypeMembers, dataTypeMappedUdt, dataTypeTags,
                   memberDataType, memberController,
                   tagOwner, tagOwnerKind, tagDataType, scadaTagRows,
                   outRows + inRows AS influenceRows,
                   tagReadByRoutines, tagWrittenByRoutines,
                   calculatedFromRows, feedsRows, robotSignalRows,
                   plcTagScadaRows,
                   ${strayLabelsCypherExpr('n', 'labelList')} AS strayLabels
          `
